/* eslint-disable camelcase */
import { h, Fragment } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import './add-to-cart-button.css';

const BisModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [product, setProduct] = useState({});
  const [oosVariants, setOosVariants] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [email, setEmail] = useState(null);
  const [phone, setPhone] = useState(null);
  const [formSuccess, setFormSuccess] = useState('');
  const [formError, setFormError] = useState('');
  const [emailError, setEmailError] = useState('');

  const handleBisModal = (event) => {
    const productData = event.detail?.payload?.product;
    const variants = event.detail?.payload?.product?.variants.edges;
    const unavailableVariants = variants
      .filter((variant) => !variant.node.available)
      .map((variant) => ({
        id: variant.node.id.split('/').pop(),
        title: variant.node.title,
        sku: variant.node.sku,
      }));

    setProduct({
      id: productData.id.split('/').pop(),
      title: productData.title,
    });

    setOosVariants(unavailableVariants);
    setSelectedVariant(unavailableVariants[0]);

    // Modal positioning is now handled by CSS flexbox centering

    setIsOpen(true);
  };

  const staralizePhoneNumber = (input) =>
    `+1${input.replaceAll('(', '').replaceAll(')', '').replaceAll(' ', '')}`;

  const formatPhone = (value) => {
    let input = value.replace(/\D/g, '');
    const size = input.length;
    if (size > 0) {
      input = `(${input}`;
    }
    if (size > 3) {
      input = `${input.slice(0, 4)}) ${input.slice(4, 11)}`;
    }
    if (size > 6) {
      input = `${input.slice(0, 9)} ${input.slice(9)}`;
    }
    return input;
  };

  const validateEmail = (emailInput) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailInput);
  };

  const handleVariantChange = (event) => {
    setSelectedVariant(
      oosVariants.find((variant) => variant.id === event.target.value),
    );
  };

  const handleEmailChange = (value) => {
    if (!value.length) {
      setEmail(null);
      setEmailError('');
      return;
    }
    if (validateEmail(value)) {
      setEmail(value);
      setEmailError('');
      setFormError('');
    } else {
      setEmailError('Invalid email address');
    }
  };

  const handlePhoneChange = (value) => {
    const formattedNumber = formatPhone(value);
    setPhone(staralizePhoneNumber(formattedNumber));
  };

  const handleBisSubmit = async () => {
    if (!phone && !email) {
      setFormError('Please enter a valid phone number and email address');
      return;
    }
    setFormError('');
    const response = await window.smsBumpBackInStock.subscribe({
      phone,
      email,
      country: 'US',
      timezone: Intl?.DateTimeFormat().resolvedOptions().timeZone,
      variant: selectedVariant,
      product,
    });

    if (response.status !== 'success') {
      setFormError('Something went wrong. Please try again later.');
    } else {
      setFormSuccess(
        'You have been successfully subscribed to back in stock notifications.',
      );
    }
  };

  useEffect(() => {
    document.addEventListener('bis:add', handleBisModal);

    return () => {
      document.removeEventListener('bis:add', handleBisModal);
    };
  }, []);

  return (
    <div
      role="dialog"
      className={`fixed inset-0 z-50 ${
        isOpen
          ? 'opacity-100 pointer-events-auto'
          : 'opacity-0 pointer-events-none'
      }`}
      tabindex="-1"
      aria-hidden="false"
    >
      <div
        className="absolute inset-0 bg-black bg-opacity-30"
        onClick={() => setIsOpen(false)}
      ></div>
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="bg-white w-full max-w-md rounded-sm p-3 relative">
          <div className="flex justify-end">
            <div
              className="header_row__close_btn"
              onClick={() => setIsOpen(false)}
            >
              <span className="close_btn">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99999 7.05732L11.3 3.75732L12.2427 4.69999L8.94266 7.99999L12.2427 11.3L11.3 12.2427L7.99999 8.94266L4.69999 12.2427L3.75732 11.3L7.05732 7.99999L3.75732 4.69999L4.69999 3.75732L7.99999 7.05732Z"
                    fill="#1C1D21"
                  ></path>
                </svg>
              </span>
            </div>
          </div>

          <div className="text-center uppercase text-sm font-bold">
            Notify Me When Available
          </div>
          <div className="text-center text-gray-400 text-sm">
            We'll notify you when this product is back in stock!
          </div>

          <div className="text-left text-gray-600 text-sm font-bold mt-3">
            <label>{product.title}</label>
            <div className="mt-1">
              <select
                onChange={handleVariantChange}
                className="w-full text-gray-600 placeholder-gray-300 border border-gray-400 hover:border-gray-500 focus:outline-none focus:shadow-outline text-sm rounded-[6px] mb-3"
              >
                {oosVariants.map((variant) => (
                  <option
                    selected={selectedVariant?.title === variant.title}
                    value={variant.id}
                  >
                    {variant.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col">
              <label className="text-left text-gray-600 font-bold mb-1">
                Notify me by text
              </label>
              <input
                name="phone"
                type="tel"
                id="phone"
                className="w-full placeholder-gray-300 border border-gray-400 hover:border-gray-500 focus:outline-none focus:shadow-outline text-sm rounded-[6px]"
                onChange={(e) => handlePhoneChange(e.target.value)}
                placeholder="Enter your phone"
                aria-label="Phone"
              />
            </div>

            <div className="flex flex-col mt-3">
              <label className="input__label text-left text-gray-600 font-bold mb-1">
                Notify me by email
              </label>
              <input
                name="email"
                type="text"
                id="email"
                className="w-full placeholder-gray-300 border border-gray-400 hover:border-gray-500 focus:outline-none focus:shadow-outline text-sm rounded-[6px]"
                onChange={(e) => handleEmailChange(e.target.value)}
                placeholder="Enter your email"
                aria-label="Email"
              />
              {emailError && (
                <div className="text-red text-sm">{emailError}</div>
              )}
            </div>
          </div>

          <div className="footer mt-6">
            {formSuccess && (
              <div className="text-green-900 text-sm">{formSuccess}</div>
            )}
            {formError && <div className="text-red text-sm">{formError}</div>}
            <button
              type="button"
              className="mt-3 flex items-center justify-center cursor-pointer w-full text-center h-[50px] bg-black rounded-[3px] text-white text-sm uppercase"
              onClick={handleBisSubmit}
            >
              Notify Me When Available
            </button>
            <div className="text-[10px] text-gray-500 text-center mt-2">
              By signing up via text you agree to receive recurring automated
              marketing messages and shopping cart reminders at the phone number
              provided. Consent is not a condition of purchase. Reply STOP to
              unsubscribe. HELP for help. Msg frequency varies. Msg &amp; Data
              rates may apply. View{' '}
              <a
                href="/pages/privacy-and-terms"
                rel="noreferrer"
                target="_blank"
                style="text-decoration: underline;"
              >
                Privacy Policy &amp; ToS
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BisModal;
